import {
  dateCell,
  moneyCell,
  pillCell,
  readonlyTextCell,
} from "@dashboard/components/Datagrid/customCells/cells";
import { AvailableColumn } from "@dashboard/components/Datagrid/types";
import { getStatusColor } from "@dashboard/misc";
import { Sort } from "@dashboard/types";
import { getColumnSortDirectionIcon } from "@dashboard/utils/columns/getColumnSortDirectionIcon";
import { GridCell, Item } from "@glideapps/glide-data-grid";
import { DefaultTheme } from "@saleor/macaw-ui-next";
import { IntlShape } from "react-intl";

import { CheckoutListUrlSortField } from "../../urls";
import {
  getGateways,
  getHeldMoney,
  getItemCount,
  getLatestEventMessage,
  getReconciliationInfo,
  PaymentRecoveryCheckout,
} from "../../utils";
import { columnsMessages, messages } from "./messages";

const COMMON_CELL_PROPS = { cursor: "pointer" as const };

/**
 * Column order mirrors the order list — when, who, how it was paid, what state
 * the money is in, how much, which channel — so the two lists read the same way.
 */
export const paymentRecoveryListStaticColumnsAdapter = (
  intl: IntlShape,
  sort: Sort<CheckoutListUrlSortField>,
): AvailableColumn[] =>
  [
    {
      id: "date",
      title: intl.formatMessage(columnsMessages.date),
      width: 300,
    },
    {
      id: "customer",
      title: intl.formatMessage(columnsMessages.customer),
      width: 250,
    },
    {
      id: "gateway",
      title: intl.formatMessage(columnsMessages.gateway),
      width: 200,
    },
    {
      id: "status",
      title: intl.formatMessage(columnsMessages.status),
      width: 150,
    },
    {
      id: "held",
      title: intl.formatMessage(columnsMessages.held),
      width: 150,
    },
    {
      id: "channel",
      title: intl.formatMessage(columnsMessages.channel),
      width: 200,
    },
    {
      id: "lastActivity",
      title: intl.formatMessage(columnsMessages.lastActivity),
      width: 300,
    },
    {
      id: "lastEvent",
      title: intl.formatMessage(columnsMessages.lastEvent),
      width: 350,
    },
    {
      id: "items",
      title: intl.formatMessage(columnsMessages.items),
      width: 100,
    },
  ].map(column => ({
    ...column,
    icon: getColumnSortDirectionIcon(sort, column.id),
  }));

export const createGetCellContent =
  ({
    checkouts,
    columns,
    intl,
    currentTheme,
  }: {
    checkouts: PaymentRecoveryCheckout[];
    columns: AvailableColumn[];
    intl: IntlShape;
    currentTheme: DefaultTheme;
  }) =>
  ([column, row]: Item): GridCell => {
    const rowData = checkouts[row];
    const columnId = columns[column]?.id;

    if (!columnId || !rowData) {
      return readonlyTextCell("");
    }

    const held = getHeldMoney(rowData.transactions);
    // Captured money needs a refund, a bare authorization only needs a void —
    // the list has to say which, or the remedy is a coin flip.
    const isCaptured = held.charged.amount > 0;
    const money = isCaptured ? held.charged : held.authorized;

    switch (columnId) {
      case "date":
        return dateCell(rowData.created, COMMON_CELL_PROPS);
      case "lastActivity":
        return dateCell(rowData.lastChange, COMMON_CELL_PROPS);
      case "customer":
        return readonlyTextCell(rowData.email ?? "-");
      case "gateway":
        return readonlyTextCell(getGateways(rowData.transactions).join(", ") || "-");
      case "status": {
        // The sweep (wsm-app-platform) escalating a row to needs_review is
        // a stronger, more actionable signal than the plain
        // captured/authorized split below -- surface it in the same
        // column rather than requiring staff to open every row to find
        // the ones that actually need a human.
        const reconciliation = getReconciliationInfo(rowData.metadata);

        if (reconciliation?.status === "needs_review") {
          return pillCell(
            intl.formatMessage(messages.needsReview),
            getStatusColor({ status: "error", currentTheme }),
            COMMON_CELL_PROPS,
          );
        }

        return pillCell(
          intl.formatMessage(isCaptured ? messages.captured : messages.authorized),
          getStatusColor({ status: isCaptured ? "error" : "warning", currentTheme }),
          COMMON_CELL_PROPS,
        );
      }
      case "held":
        return moneyCell(money.amount, money.currency, {
          ...COMMON_CELL_PROPS,
          readonly: true,
        });
      case "channel":
        return readonlyTextCell(rowData.channel.name);
      case "lastEvent":
        return readonlyTextCell(getLatestEventMessage(rowData.transactions));
      case "items":
        return readonlyTextCell(String(getItemCount(rowData.lines)));
      default:
        return readonlyTextCell("");
    }
  };

/** Saleor only sorts checkouts by creation date, customer and payment. */
export const canBeSorted = (columnId: string): columnId is CheckoutListUrlSortField =>
  Object.values(CheckoutListUrlSortField).includes(columnId as CheckoutListUrlSortField);
